/**
 * Reading theme colours out of the page, for the ad-wall shader.
 *
 * Separate from the component because none of it is React: it is CSS colour
 * parsing, and the shader needs numbers rather than tokens.
 */

/**
 * Any CSS colour to sRGB 0–1.
 *
 * Deliberately NOT linearised. The shader mixes toward `u_bg` and writes
 * straight to an sRGB framebuffer, so linearising here would leave the canvas
 * a different shade from the CSS background behind it — the one thing that
 * would make the at-rest state visible. A 2D context is the parser because
 * tokens resolve to hex, `rgb()` or `hsl()` depending on the token, and
 * `fillStyle` normalises all three.
 */
export function parseColor(value: string): [number, number, number] | null {
  const cv = document.createElement("canvas");
  cv.width = 1;
  cv.height = 1;
  const c = cv.getContext("2d");
  if (!c) return null;
  const v = value.trim();
  // `transparent` / `rgba(0,0,0,0)` means the element paints nothing, so it is
  // not an answer to "what colour is behind the canvas" — fall through.
  if (!v || v === "transparent" || /^rgba\(0,\s*0,\s*0,\s*0\)$/.test(v))
    return null;
  c.fillStyle = "#000";
  c.fillStyle = v;
  c.fillRect(0, 0, 1, 1);
  const [r, g, b] = c.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
}

/** The two stops of `--nf-ds-brand-grad`, with palette tokens as the fallback. */
export function readGradient(
  cs: CSSStyleDeclaration,
): [[number, number, number], [number, number, number]] {
  const grad = cs.getPropertyValue("--nf-ds-brand-grad");
  const stops = grad.match(/#[0-9a-f]{3,8}\b|rgba?\([^)]*\)|hsla?\([^)]*\)/gi);
  if (stops && stops.length >= 2) {
    const a = parseColor(stops[0]);
    const b = parseColor(stops[stops.length - 1]);
    if (a && b) return [a, b];
  }
  return [
    parseColor(cs.getPropertyValue("--nf-ds-purple-product")) ?? [
      0.56, 0.11, 0.96,
    ],
    parseColor(cs.getPropertyValue("--nf-ds-magenta")) ?? [0.99, 0.38, 1],
  ];
}
